import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchIacrmClients,
  fetchIacrmInvoices,
  fetchIacrmInvoiceSummary,
  fetchIacrmPipelineProspects,
  fetchIacrmPipelineStages,
  fetchIacrmRequestLogs,
  type IacrmRequestLogFilters,
  fetchIacrmPlatformBusinessClients,
  fetchIacrmPlatformBusinessPipelineProspects,
  fetchIacrmPlatformBusinessPipelineStages,
  fetchIacrmPlatformBusinesses,
  fetchIacrmPlatformBusinessServices,
  fetchIacrmServices,
  getIacrmConfig,
  hasIacrmConfig,
  saveIacrmConfig,
  testIacrmConnection,
} from './api'
import type {
  IacrmApiConfig,
  IacrmPipelineStage,
  IacrmPipelineStageSummary,
} from '../../types/iacrm'
import { IACRM_STORE_EVENT, getLocalIacrmProspects } from './prospectStore'

function isConfigured() {
  return hasIacrmConfig()
}

// ---------------------------------------------------------------------------
// Local store subscriber — re-renders when the prospect store changes
// ---------------------------------------------------------------------------

function useLocalIacrmProspects() {
  const [localProspects, setLocalProspects] = useState(() => getLocalIacrmProspects())
  useEffect(() => {
    const handler = () => setLocalProspects(getLocalIacrmProspects())
    window.addEventListener(IACRM_STORE_EVENT, handler)
    return () => window.removeEventListener(IACRM_STORE_EVENT, handler)
  }, [])
  return localProspects
}

// ---------------------------------------------------------------------------
// Connection test mutation
// ---------------------------------------------------------------------------

export function useTestIacrmConnection() {
  return useMutation({
    mutationFn: async () => {
      const result = await testIacrmConnection()
      const config = getIacrmConfig()
      if (config) {
        saveIacrmConfig({
          ...config,
          connection_status: result.ok ? 'connected' : 'failed',
          last_tested_at: new Date().toISOString(),
        } satisfies IacrmApiConfig)
      }
      return result
    },
  })
}

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

export function useIacrmServices() {
  return useQuery({
    queryKey: ['iacrm', 'services'],
    queryFn: fetchIacrmServices,
    enabled: isConfigured(),
  })
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export function useIacrmClients() {
  return useQuery({
    queryKey: ['iacrm', 'clients'],
    queryFn: fetchIacrmClients,
    enabled: isConfigured(),
  })
}

// ---------------------------------------------------------------------------
// Pipeline — raw mock hooks
// ---------------------------------------------------------------------------

export function useIacrmPipeline(stage?: string) {
  return useQuery({
    queryKey: ['iacrm', 'pipeline', 'prospects', stage],
    queryFn: () => fetchIacrmPipelineProspects(stage),
    enabled: false,
  })
}

export function useIacrmPipelineStages() {
  return useQuery({
    queryKey: ['iacrm', 'pipeline', 'stages'],
    queryFn: fetchIacrmPipelineStages,
    enabled: false,
  })
}

const PIPELINE_STAGE_LABELS: Record<IacrmPipelineStage, string> = {
  suspect: 'Suspect',
  prospect_froid: 'Prospect Froid',
  prospect_tiede: 'Prospect Tiède',
  prospect_chaud: 'Prospect Chaud',
  converted: 'Converti',
  lost: 'Perdu',
}

const ORDERED_STAGES: IacrmPipelineStage[] = [
  'suspect',
  'prospect_froid',
  'prospect_tiede',
  'prospect_chaud',
  'converted',
  'lost',
]

// ---------------------------------------------------------------------------
// Pipeline — local-only hooks (IACRM pull endpoints not available)
// ---------------------------------------------------------------------------

export function useIacrmPipelineMerged(stage?: string) {
  const localProspects = useLocalIacrmProspects()

  const filtered = useMemo(
    () => (stage ? localProspects.filter((p) => p.stage === stage) : localProspects),
    [localProspects, stage],
  )

  return {
    data: { data: filtered },
    isLoading: false,
    isPending: false,
    isError: false,
    error: null,
  }
}

export function useIacrmStagesMerged() {
  const localProspects = useLocalIacrmProspects()

  const stages = useMemo<IacrmPipelineStageSummary[]>(() => {
    const counts: Partial<Record<IacrmPipelineStage, number>> = {}
    for (const p of localProspects) {
      counts[p.stage] = (counts[p.stage] ?? 0) + 1
    }
    return ORDERED_STAGES.map((s) => ({
      stage: s,
      label: PIPELINE_STAGE_LABELS[s],
      count: counts[s] ?? 0,
    }))
  }, [localProspects])

  return {
    data: { data: stages },
    isLoading: false,
    isPending: false,
    isError: false,
    error: null,
  }
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export function useIacrmInvoices(status?: string) {
  return useQuery({
    queryKey: ['iacrm', 'invoices', status],
    queryFn: () => fetchIacrmInvoices(status),
    enabled: isConfigured(),
  })
}

export function useIacrmInvoiceSummary() {
  return useQuery({
    queryKey: ['iacrm', 'invoices', 'summary'],
    queryFn: fetchIacrmInvoiceSummary,
    enabled: isConfigured(),
  })
}

// ---------------------------------------------------------------------------
// Operations logs
// ---------------------------------------------------------------------------

export function useIacrmRequestLogs(filters: IacrmRequestLogFilters) {
  return useQuery({
    queryKey: ['iacrm', 'request-logs', filters],
    queryFn: () => fetchIacrmRequestLogs(filters),
    enabled: isConfigured(),
    refetchInterval: 15000,
  })
}

// ---------------------------------------------------------------------------
// Platform (superadmin) — cross-business IACRM overview
// ---------------------------------------------------------------------------

export function useIacrmPlatformBusinesses() {
  return useQuery({
    queryKey: ['iacrm', 'platform', 'businesses'],
    queryFn: fetchIacrmPlatformBusinesses,
    enabled: isConfigured(),
  })
}

export function useIacrmPlatformBusinessServices(businessId: string | null) {
  return useQuery({
    queryKey: ['iacrm', 'platform', 'businesses', businessId, 'services'],
    queryFn: () => fetchIacrmPlatformBusinessServices(businessId!),
    enabled: isConfigured() && !!businessId,
  })
}

export function useIacrmPlatformBusinessClients(businessId: string | null) {
  return useQuery({
    queryKey: ['iacrm', 'platform', 'businesses', businessId, 'clients'],
    queryFn: () => fetchIacrmPlatformBusinessClients(businessId!),
    enabled: isConfigured() && !!businessId,
  })
}

export function useIacrmPlatformBusinessPipeline(businessId: string | null, stage?: string) {
  return useQuery({
    queryKey: ['iacrm', 'platform', 'businesses', businessId, 'pipeline', stage],
    queryFn: () => fetchIacrmPlatformBusinessPipelineProspects(businessId!, stage),
    enabled: isConfigured() && !!businessId,
  })
}

export function useIacrmPlatformBusinessPipelineStages(businessId: string | null) {
  return useQuery({
    queryKey: ['iacrm', 'platform', 'businesses', businessId, 'pipeline', 'stages'],
    queryFn: () => fetchIacrmPlatformBusinessPipelineStages(businessId!),
    enabled: isConfigured() && !!businessId,
  })
}
